import { datadogLogs } from "@datadog/browser-logs";
import type { Logger } from "@pepperstone/mfe-dev-kit/types";

// Local dev: also mirror to the browser console for immediate visibility.
// In deployed builds we rely solely on Datadog Browser Logs.
const isDev = !import.meta.env.PROD;

type DdLevel = "debug" | "info" | "warn" | "error";

// Call sites pass either a single structured-context object
// (logger.error("msg", { event, ... })) or loose varargs. Normalise both into
// the single `messageContext` object the Datadog Logs SDK expects.
const toContext = (args: unknown[]): object | undefined => {
  if (args.length === 0) {
    return undefined;
  }
  const [first] = args;
  if (args.length === 1 && typeof first === "object" && first !== null) {
    return first as object;
  }
  return { args };
};

const forward = (level: DdLevel, message: string, args: unknown[]) => {
  // No-op until datadogLogs.init() runs (see observability/datadog.ts); safe
  // to call regardless.
  datadogLogs.logger[level](message, toContext(args));
  if (isDev) {
    console[level](message, ...args);
  }
};

/**
 * App-wide logger backed by Datadog Browser Logs (100% sampled), replacing the
 * WebX host child logger.
 *
 * Implements the MFE `Logger` shape so it drops into every existing
 * `useLogger()` / logger-prop call site unchanged. Unlike the host child logger
 * (which only forwarded `error` and above to Datadog), `warn`/`info`/`debug`
 * now reach Datadog too. `fatal` maps to Datadog's `error` level.
 */
export const logger: Logger = {
  debug: (message, ...args) => forward("debug", message, args),
  info: (message, ...args) => forward("info", message, args),
  warn: (message, ...args) => forward("warn", message, args),
  error: (message, ...args) => forward("error", message, args),
  fatal: (message, ...args) => forward("error", message, args),
};
